import React, { useState, useRef, useEffect } from "react";
import { motion, useAnimation } from "framer-motion";
import tadStart from "../../@shared/assets/toshi-start.png";
import tadFin from "../../@shared/assets/toshi-fin.png";
import fightBg from "../../@shared/assets/fightbg.gif";
import AudioManager from "../../@shared/utils/AudioManager";

const FightAnimation = ({ attackAnimation, character, onFinish }) => {
  const [strikePose, setStrikePose] = useState(false);
  const animationCooldownRef = useRef(false);
  const characterControls = useAnimation();
  const flashControls = useAnimation();
  const imageStart = character?.imageStart;
  const imageFin = character?.imageFin;
  const screenWidth = window.innerWidth;
  // const targetX = screenWidth / 2 - 170; // same as native logic
  const targetX = 0; // same as native logic

  // Helper function for delays
  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const finishAnimation = () => {
    setStrikePose(false);
    if (onFinish) onFinish();
  };

  const startAttackAnimation = async () => {
    // Move from off-screen (-400) to targetX over 250ms.
    await characterControls.start(
      { x: targetX },
      { duration: 0.25, ease: "easeInOut" }
    );
    // Switch to strike pose.
    setStrikePose(true);
    AudioManager.playSFX("playerHit");

    // Run flash and shake animations concurrently:
    await Promise.all([
      flashControls.start(
        { opacity: [0, 1, 0, 1, 0] },
        { duration: 0.2, ease: "linear" }
      ),
      characterControls.start(
        { x: [targetX, targetX - 10, targetX + 10, targetX - 5, targetX] },
        { duration: 0.2, ease: "easeInOut" }
      ),
    ]);

    // Wait before retreating.
    await delay(1500);

    // Retreat animation: move character back off-screen to -400 over 300ms.
    await characterControls.start(
      { x: -400 },
      { duration: 0.3, ease: "easeInOut" }
    );
    finishAnimation();
  };

  useEffect(() => {
    if (!attackAnimation) return;
    if (animationCooldownRef.current) return;

    animationCooldownRef.current = true;
    // Start animation after a brief delay.
    setTimeout(() => {
      startAttackAnimation();
    }, 75);
    // Reset cooldown after total animation duration (approx. 2000ms)
    setTimeout(() => {
      animationCooldownRef.current = false;
    }, 2000);
  }, [attackAnimation]);

  return (
    <div
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        pointerEvents: "none",
      }}
    >
      {/* Background Layer */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `url(${fightBg})`,
          backgroundSize: "cover",
          opacity: 0.7,
          zIndex: 0,
        }}
      />
      {/* Foreground Layer */}
      <div
        style={{
          position: "relative",
          zIndex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
        }}
      >
        {/* Flash Overlay */}
        <motion.div
          animate={flashControls}
          initial={{ opacity: 0 }}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "#fff",
            zIndex: 5,
          }}
        />
        {/* Character Image */}
        <motion.img
          src={strikePose ? imageFin : imageStart}
          animate={characterControls}
          initial={{ x: -400 }}
          style={{
            width: "250px",
            height: "250px",
            position: "absolute",
            bottom: "35%",
            zIndex: 2,
          }}
          alt=""
        />
      </div>
    </div>
  );
};

export default FightAnimation;
